import type { FC, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@packages/shared/src/ui/Button/Button';
import { type FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterSettingsDesktopFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsDesktopFragmentFragmentDoc';
import { Popover, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@packages/shared/src/ui/Collapsible/Collapsible';
import { useMediaTabletType } from '@packages/shared/src/hooks/useMediaTabletType/useMediaTabletType';
import type { FilterSettingsDesktopFragmentFragment } from '@packages/gql/generated/shopping/graphql';

import type { SelectedFilterValue } from '../../types';
import { FilterSettingsDesktopButton } from '../FilterSettingsDesktopButton/FilterSettingsDesktopButton';
import { useFilterSettingsPopover } from './useFilterSettingsPopover';
import { DynamicFilterSettingsDesktopPopover } from './FilterSettingsDesktopPopover.dynamic';
import { PRODUCT_LIST_ID } from '../../ids';

/* GraphQL */ `
  fragment FilterSettingsDesktopFragment on SearchFilterableAndSortableResult {
    id
    availableFilters {
      id
      displayName
      ...FilterSettingsDesktopPopoverFragment
    }
  }
`;

export const filterMessages = defineMessages({
  collapseFiltersLabel: {
    defaultMessage: 'Weniger anzeigen',
    description: 'button label for the desktop filter settings collapse button',
    id: 'filterSettingsDesktop.collapseButtonLabel',
  },
  expandFiltersLabel: {
    defaultMessage: 'Mehr anzeigen ({filterCount})',
    description: 'button label for the desktop filter settings expand button',
    id: 'filterSettingsDesktop.expandButtonLabel',
  },
  filtersProductList: {
    id: 'filterSettings.productList.label',
    defaultMessage: 'Produktfilter',
  },
});

export type FilterSettingsDesktopProps = {
  maskedData: FragmentType<typeof FilterSettingsDesktopFragmentFragmentDoc>;
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (selectedValues: SelectedFilterValue[]) => void;
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
};

type FilterItem = FilterSettingsDesktopFragmentFragment['availableFilters'][number];

const CollapseButton: FC<{
  children: ReactNode;
  iconComponent: ReactNode;
  onClick?: () => void;
}> = ({ children, iconComponent, onClick }) => (
  <Button className="flex h-[2.25rem] w-full px-1" onClick={onClick}>
    <div className="mr-auto w-full overflow-hidden text-left leading-normal hyphens-auto">
      {children}
    </div>
    {iconComponent}
  </Button>
);

const useCollapseTransition = () => {
  const [state, setState] = useState<'collapsed' | 'expanded'>('collapsed');

  const showMore = () => {
    setState('expanded');
  };

  const showLess = () => {
    setState('collapsed');
  };

  return {
    showingAllFilters: state !== 'collapsed',
    showMore,
    showLess,
  };
};
/**
 * FilterSettingsDesktop component description displayed in storybook
 * */
export const FilterSettingsDesktop: FC<FilterSettingsDesktopProps> = ({
  maskedData,
  onSubmit,
  selectedFilterValues: initiallySelectedFilterValues,
}) => {
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  const data = unmask<typeof FilterSettingsDesktopFragmentFragmentDoc>(maskedData);

  const { showingAllFilters, showMore, showLess } = useCollapseTransition();

  const isTablet = useMediaTabletType();

  const tenthFilterButtonRef = useRef<HTMLButtonElement>(null);

  const focusTenthFilterButton = () => {
    // using timeout to ensure that the button is focused after the collapse animation has finished
    setTimeout(() => {
      if (tenthFilterButtonRef.current) {
        tenthFilterButtonRef.current.focus();
      }
    }, 0);
  };

  const handleShowMore = () => {
    showMore();
    focusTenthFilterButton();
  };

  const columns = isTablet ? 4 : 5;
  const maxFiltersWithoutCollapse = 2 * columns;
  const isCollapsible = data.availableFilters.length > maxFiltersWithoutCollapse;
  const collapsedFilterCount = maxFiltersWithoutCollapse - 1;
  const additionalFilterCount = data.availableFilters.length - collapsedFilterCount;
  const filtersWithoutCollapse = isCollapsible
    ? data.availableFilters.slice(0, collapsedFilterCount)
    : data.availableFilters;
  const remainingCollapsedFilters = data.availableFilters.slice(collapsedFilterCount);

  const {
    activeFilterIds,
    selectedFilterId,
    isPopoverOpen,
    pendingFilterValues,
    hasChanges,
    selectFilter,
    closePopover,
    toggleFilterValue,
    setFilterRange,
  } = useFilterSettingsPopover(initiallySelectedFilterValues);

  const selectedFilter = data.availableFilters.find((filter) => filter.id === selectedFilterId);
  const { formatMessage } = useIntl();

  const renderFilterButton = (filter: FilterItem, index: number) => (
    <Popover
      open={openFilterId === filter.id}
      onOpenChange={(isOpen) => {
        selectFilter(isOpen ? filter.id : '');
        setOpenFilterId(isOpen ? filter.id : null);
      }}
      key={filter.id}
    >
      <PopoverTrigger asChild>
        <FilterSettingsDesktopButton
          isActive={activeFilterIds.includes(filter.id)}
          isPopoverOpen={selectedFilterId === filter.id && isPopoverOpen}
          onClick={() => {
            selectFilter(filter.id);
          }}
          ref={index === 9 ? tenthFilterButtonRef : undefined}
        >
          {filter.displayName}
        </FilterSettingsDesktopButton>
      </PopoverTrigger>
      <DynamicFilterSettingsDesktopPopover
        requestCacheKey={data.id}
        maskedData={selectedFilter}
        pendingFilterValues={pendingFilterValues}
        hasChanges={hasChanges}
        closePopover={() => {
          closePopover();
          setOpenFilterId(null);
        }}
        onToggle={toggleFilterValue}
        onSetRange={setFilterRange}
        onSubmit={(values) => {
          onSubmit?.(values);
          setOpenFilterId(null);
        }}
      />
    </Popover>
  );

  return (
    <nav
      id="product-filters"
      aria-label={formatMessage(filterMessages.filtersProductList)}
      aria-controls={PRODUCT_LIST_ID}
    >
      <Collapsible>
        <div className="grid grid-cols-5 gap-2 md:max-lg:grid-cols-4">
          {filtersWithoutCollapse.map((filter, index) => renderFilterButton(filter, index))}
          {remainingCollapsedFilters.length > 1 && (
            <CollapsibleContent className="contents">
              {remainingCollapsedFilters.map((filter, index) => renderFilterButton(filter, index))}
            </CollapsibleContent>
          )}
          <CollapsibleTrigger asChild>
            {isCollapsible &&
              (showingAllFilters ? (
                <CollapseButton
                  iconComponent={<span className="icon-emp-minus size-4" />}
                  onClick={showLess}
                >
                  <FormattedMessage
                    key="collapseFiltersLabel-text"
                    {...filterMessages.collapseFiltersLabel}
                  />
                </CollapseButton>
              ) : (
                <CollapseButton
                  iconComponent={<span className="icon-emp-plus size-4" />}
                  onClick={handleShowMore}
                >
                  <FormattedMessage
                    key="expandFiltersLabel-text"
                    {...filterMessages.expandFiltersLabel}
                    values={{ filterCount: additionalFilterCount }}
                  />
                </CollapseButton>
              ))}
          </CollapsibleTrigger>
        </div>
      </Collapsible>
    </nav>
  );
};
